import { auth, firestoreDb } from '@/lib/firebase';
import api from '@/lib/api';

const GETTERS = {
  CONTEXT: 'GET_CONTEXT',
  IS_TEAM_MANAGER: 'GET_IS_TEAM_MANAGER',
  IS_TEAM_MEMBER: 'GET_IS_TEAM_MEMBER',
  IS_TEAM_OWNER: 'GET_IS_TEAM_OWNER',
  ROLE: 'GET_ROLE',
  PERMISSIONS: 'GET_PERMISSIONS',
  TEAM_ID: 'GET_TEAM_ID',
  TEAMS_CHECKED: 'GET_TEAMS_CHECKED',
  TEAMS_COUNT: 'GET_TEAMS_COUNT',
};

const MUTATIONS = {
  CONTEXT: 'SET_CONTEXT',
  SUBSCRIBERS: 'SET_SUBSCRIBERS',
  TEAMS_CHECKED: 'SET_TEAMS_CHECKED',
  TEAMS_COUNT: 'SET_TEAMS_COUNT',
};

const RESET = 'RESET';

const getDefaultState = () => ({
  context: null, // { type: 'self' | 'team', teamId, role, permissions }
  subscribers: [],
  teamsChecked: false, // Optional: to track if user has selected a team context
  teamsCount: 0,
});

export const state = getDefaultState();

export const mutations = {
  [MUTATIONS.CONTEXT](state, context) {
    state.context = context;
  },
  [MUTATIONS.SUBSCRIBERS](state, subscribers) {
    state.subscribers = subscribers;
  },
  [MUTATIONS.TEAMS_CHECKED](state, checked) {
    state.teamsChecked = checked;
  },
  [MUTATIONS.TEAMS_COUNT](state, count) {
    state.teamsCount = count;
  },
  [RESET](state) {
    Object.assign(state, getDefaultState());
  },
};

export const getters = {
  [GETTERS.CONTEXT](state) {
    return state.context;
  },
  [GETTERS.IS_TEAM_MANAGER](state) {
    return state.context?.role
        && state.context?.role !== 'user';
  },
  [GETTERS.IS_TEAM_MEMBER](state) {
    return !!state.context?.role;
  },
  [GETTERS.IS_TEAM_OWNER](state) {
    return state.context?.role === 'owner';
  },
  [GETTERS.ROLE](state) {
    return state.context?.role || null;
  },
  [GETTERS.PERMISSIONS](state) {
    return state.context?.permissions || [];
  },
  [GETTERS.TEAM_ID](state) {
    return state.context?.teamId || null;
  },
  [GETTERS.TEAMS_CHECKED](state) {
    return state.teamsChecked;
  },
  [GETTERS.TEAMS_COUNT](state) {
    return state.teamsCount;
  },
};

export const actions = {

  // Members

  async fetchMember(_, { teamId, id }) {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await api.get(`/teams/${teamId}/members/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },
  async fetchMemberProfile(_, { teamId, id }) {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await api.get(`/teams/${teamId}/members/${id}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async addMember({ getters }, { files = [], specFiles = [], userInfo }) {
    const token = await auth.currentUser.getIdToken();
    const context = getters[GETTERS.CONTEXT];
    const teamId = context.realTeamId;
    let newUserId;

    try {
      const response = await api.post(
        `/teams/${teamId}/member/new`,
        userInfo,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );

      newUserId = response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', newUserId);

      try {
        await api.post(
          `/teams/${teamId}/member/file`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } catch (error) {
        if (error.response) {
          throw error.response.data;
        }

        throw error;
      }
    }

    for (const file of specFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', newUserId);
      formData.append('isSpec', true);

      try {
        await api.post(
          `/teams/${teamId}/member/file`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } catch (error) {
        if (error.response) {
          throw error.response.data;
        }

        throw error;
      }
    }

    if (files.length || specFiles.length) {
      try {
        const docs = files.map((file) => file.name);
        const specDocs = specFiles.map((file) => file.name);
  
        await api.post(
          `/teams/${teamId}/member/validate`,
          {
            userId: newUserId,
            docs,
            specDocs,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        );
      } catch (error) {
        if (error.response) {
          throw error.response.data;
        }
  
        throw error;
      }
    }
  },

  async updateMemberPermissions(_, { teamId, userId, permissions, grantedScopesByType }) {
    const token = await auth.currentUser.getIdToken();
    const payload = {};

    if (permissions) {
      payload.permissions = permissions
    };

    if (grantedScopesByType) {
      payload.grantedScopesByType = grantedScopesByType;
    };


    try {
      const response = await api.put(`/teams/${teamId}/members/${userId}/permissions`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async updateMemberRole(_, { teamId, userId, role }) {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await api.put(`/teams/${teamId}/members/${userId}/role`, { role }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  async enableMember(_, { teamId, userId }) {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await api.put(
        `/teams/${teamId}/members/${userId}/enable`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async disableMember(_, { teamId, userId }) {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await api.put(
        `/teams/${teamId}/members/${userId}/disable`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async checkMember(_, payload) {
    try {
      const token = await auth.currentUser.getIdToken();
      const teamId = payload.teamId;
      delete payload.teamId;

      const response = await api.post(
        `/teams/${teamId}/member`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async checkMemberEmail({ getters }, email) {
    try {
      const token = await auth.currentUser.getIdToken();
      const context = getters[GETTERS.CONTEXT];
      const teamId = context.realTeamId;
      const response = await api.post(
        `/teams/${teamId}/member/email`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async fetchMembers(_, params) {
    try {
      const token = await auth.currentUser.getIdToken();
      const teamId = params.teamId;
      delete params.teamId;

      const response = await api.get(`/teams/${teamId}/members`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  async fetchMembersNames(_, params) {
    try {
      const token = await auth.currentUser.getIdToken();
      const teamId = params.teamId;
      delete params.teamId;

      const response = await api.get(
        `/teams/${teamId}/members/names`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async fetchMemberUnities({ rootGetters }, userId) {
    try {
      const token = await auth.currentUser.getIdToken();
      const context = rootGetters['team/GET_CONTEXT'];
      const response = await api.get(`/teams/${context.realTeamId}/members/${userId}/unities`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  async checkScopesForMember({ getters }, { userId, scopes }) {
    try {
      const token = await auth.currentUser.getIdToken();
      const context = getters[GETTERS.CONTEXT];
      const teamId = context.realTeamId;
      const response = await api.post(
        `/teams/${teamId}/member/${userId}/check-scopes`,
        { scopes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Teams

  async fetchOwnTeam() {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await api.get(`/teams/my-team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async fetchTeam(_, teamId) {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await api.get(`/teams/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async fetchTeams() {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await api.get(`/teams/my-teams`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async createTeam(_, payload) {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await api.post(
        '/teams',
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // services & specializations

  async fetchServices({ rootGetters }, params) {
    try {
      const token = await auth.currentUser.getIdToken();
      const context = rootGetters['team/GET_CONTEXT'];
      const response = await api.get(`/teams/${context.realTeamId}/services`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }

      throw error;
    }
  },

  async fetchSpecializations({ rootGetters }) {
    try {
      const token = await auth.currentUser.getIdToken();
      const context = rootGetters['team/GET_CONTEXT'];
      const response = await api.get(`/teams/${context.realTeamId}/specializations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Others

  reset({ commit, state }) {
    // Unsubscribe all
    if (state.subscribers && state.subscribers.length) {
      state.subscribers.forEach(unsub => unsub && unsub());
      commit(MUTATIONS.SUBSCRIBERS, []);
    }
    commit(RESET);
  },

  setContext({ commit, dispatch, rootGetters }, context) {
    commit(MUTATIONS.CONTEXT, context);
    dispatch('subscribeAll', rootGetters['user/GET_USER_UID']);
  },

  resetContext({ commit }) {
    commit(MUTATIONS.CONTEXT, null);
    commit(MUTATIONS.TEAMS_CHECKED, false);
  },

  setTeamsChecked({ commit }, checked) {
    commit(MUTATIONS.TEAMS_CHECKED, checked);
  },

  async fetchTeamSchedulerLink({ getters }) {
    try {
      const token = await auth.currentUser.getIdToken();
      const context = getters[GETTERS.CONTEXT];
      const teamId = context.realTeamId;
      const response = await api.get(`/teams/${teamId}/scheduler-link`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  async subscribeAll({ commit, dispatch, state }, userId) {
    // Unsubscribe all previous
    if (state.subscribers?.length) {
      state.subscribers.forEach(unsub => unsub && unsub());
      commit(MUTATIONS.SUBSCRIBERS, []);
    }

    const teamId = state.context?.realTeamId;

    if (!userId || !teamId) {
      return;
    }

    const membershipQuery = () => {
      return firestoreDb
        .collection('team-memberships')
        .where('teamId', '==', teamId)
        .where('userId', '==', userId)
        .limit(1)
        .onSnapshot((snapshot) => {
          const doc = snapshot.docs[0];
          const data = doc?.data();
    
          if (data && state.context) {
            if (data.isEnabled === false) {
              // Reset all team state and redirect to post-login
              dispatch('reset');
    
              if (typeof window !== 'undefined' && window?.location) {
                window.location.href = '/post-login';
              }
    
              return;
            }
            state.context = {
              ...state.context,
              role: data.role,
              permissions: data.permissions || [],
            };
          }
        }, (err) => {
          console.log('team snapshot error');
          console.log('err', err);
        });
    };

    const membershipCountQuery = () => {
      return firestoreDb
        .collection('team-memberships')
        .where('userId', '==', userId)
        .onSnapshot((snapshot) => {
          const count = snapshot.size;

          commit(MUTATIONS.TEAMS_COUNT, count);
        });
    };

    const queries = {
      'membership': membershipQuery(),
      'membershipCount': membershipCountQuery(),
    };
    const unsubscribers = [];

    for (const key of Object.keys(queries)) {
      const query = queries[key];

      if (!query) {
        continue;
      }

      unsubscribers.push(query);
    };

    commit(MUTATIONS.SUBSCRIBERS, unsubscribers);
  },

  async fetchEvents({ rootGetters }, params) {
    const token = await auth.currentUser.getIdToken();
    const context = rootGetters['team/GET_CONTEXT'];
    const teamId = context.realTeamId;

    try {
      const response = await api.get(
        `/teams/${teamId}/events`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
}; 